import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, type OnInit } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';

import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { TableModule } from 'primeng/table';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageModule } from 'primeng/message';

import { Organization } from '@features/organizations/models/organization.model';
import { OrganizationsService } from '@features/organizations/services/organizations.service';
import { AlertComponent } from '@shared/alert/alert.component';
import { Admin } from '@features/organizations/models/admin.model';
import { Invite } from '@features/organizations/models/invite.model';
import { PrimeNgSettings } from '@core/static/primeng.static';
import { ErrorCodes } from '@core/enums/errors.enum';

@Component({
  selector: 'tr-admins',
  imports: [CommonModule, AlertComponent, TableModule, Button, DialogModule, ConfirmDialogModule, MessageModule],
  providers: [ConfirmationService],
  templateUrl: './Admins.component.html',
  styleUrl: './Admins.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminsComponent implements OnInit {
  private dialogConfig = inject(DynamicDialogConfig);
  private orgService = inject(OrganizationsService);
  private confirmationService = inject(ConfirmationService);
  private messageService = inject(MessageService);
  private cdr = inject(ChangeDetectorRef);
  private clipboard = inject(Clipboard);

  organization!: Organization;
  loading: boolean = true;

  admins: Admin[] = [];
  invites: Invite[] = [];
  creatingInviteCode: boolean = false;

  constructor() {}

  ngOnInit(): void {
    if (this.dialogConfig.data?.Organization) {
      const orgInfo = this.dialogConfig.data.Organization;
      this.organization = orgInfo;

      this.orgService.getAdminsAndInvites(orgInfo.id).subscribe({
        next: ({ admins, invites }) => {
          this.admins = admins;
          this.invites = invites;
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
    }
  }

  getUsername(admin: Admin) {
    const user = admin.get('user');
    return user.get('username');
  }

  createInviteCode() {
    this.creatingInviteCode = true;

    this.orgService.createInvite(this.organization.id).subscribe({
      next: (invite) => {
        if (invite) {
          this.invites.push(invite);
          this.creatingInviteCode = false;

          this.cdr.detectChanges();

          this.messageService.add({
            severity: 'success',
            key: 'main-toast',
            summary: 'Created Invite Code',
            detail: `Successfully created an invite code`,
            life: PrimeNgSettings.MessageLife,
          });
        }
      },
      error: (err) => {
        if (err.code === ErrorCodes.InviteMaxCodes) {
          this.messageService.add({
            severity: 'error',
            key: 'main-toast',
            summary: 'Maxed out',
            detail: `Sorry, you have reached the maximum number of invite codes for this organization`,
            life: PrimeNgSettings.MessageLife,
          });
        } else {
          this.messageService.add({
            severity: 'error',
            key: 'main-toast',
            summary: 'Error',
            detail: `Sorry, an error occured attempting to create the invite code.`,
            life: PrimeNgSettings.MessageLife,
          });
        }

        this.creatingInviteCode = false;
        this.cdr.detectChanges();
      },
    });
  }

  copyCodeShortcut(invite: Invite) {
    const urlRoot = window.location.origin;
    const urlShortcut = `${urlRoot}/invite/${invite.code}`;
    this.clipboard.copy(urlShortcut);

    this.messageService.add({
      severity: 'success',
      key: 'main-toast',
      summary: 'Copied Invite Code',
      detail: `Copied URL invite code: ${urlShortcut}`,
      life: PrimeNgSettings.MessageLife,
    });
  }

  deleteInviteConfirm(event: Event, invite: Invite) {
    this.confirmationService.confirm({
      dismissableMask: true,
      message: 'Are you sure you want to delete this invite?',
      header: 'Confirmation',
      closable: true,
      closeOnEscape: true,
      accept: () => {
        this.orgService.deleteInvite(this.organization.id, invite.id).subscribe({
          next: (result) => {
            if (result) {
              this.invites = this.invites.filter((i) => i.id !== invite.id);

              this.cdr.detectChanges();

              this.messageService.add({
                severity: 'success',
                key: 'main-toast',
                summary: 'Deleted',
                detail: `Successfully deleted the invite`,
                life: PrimeNgSettings.MessageLife,
              });
            }
          },
          error: (err) => {
            this.messageService.add({
              severity: 'error',
              key: 'main-toast',
              summary: 'Failed',
              detail: `Failed to delete the invite, please try again.`,
              life: PrimeNgSettings.MessageLife,
            });
          },
        });
      },
    });
  }

  deleteAdminConfirm(event: Event, admin: Admin) {
    this.confirmationService.confirm({
      dismissableMask: true,
      message: 'Are you sure you want to remove this admin?',
      header: 'Confirmation',
      closable: true,
      closeOnEscape: true,
      accept: () => {
        this.orgService.deleteAdmin(this.organization.id, admin.id).subscribe({
          next: (result) => {
            if (result) {
              this.admins = this.admins.filter((i) => {
                i.id !== admin.id;
              });

              this.cdr.detectChanges();

              this.messageService.add({
                severity: 'success',
                key: 'main-toast',
                summary: 'Deleted',
                detail: `Successfully deleted the Admin`,
                life: PrimeNgSettings.MessageLife,
              });
            }
          },
          error: (err) => {
            this.messageService.add({
              severity: 'error',
              key: 'main-toast',
              summary: 'Failed',
              detail: `Failed to delete the Admin, please try again.`,
              life: PrimeNgSettings.MessageLife,
            });
          },
        });
      },
    });
  }
}
